import React, { useMemo } from 'react'
import { DataTable } from 'shared/components/DataTable/DataTable'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { StatusPill } from 'pages/Reports/components/StatusPill/StatusPill'
import { FiltersPanel } from 'widgets/FiltersPanel/FiltersPanel'
import FiltersBar from 'pages/ApplicationsPage/components/FiltersBar/FiltersBar'
import { ListsTabs } from 'widgets/ListsTabs/ListsTabs'
import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg'
import { ReactComponent as ListIcon } from 'assets/icons/list.svg'
import { ReactComponent as ChevronIcon } from 'assets/icons/arrow_short_right.svg'
import {
	CONTACT_TYPE_LABELS,
	LOG_STATUS_LABELS,
	STATUS_PILL_TONE,
	formatLogDateTime,
	formatRecipientsSummary,
} from '../../data/parentCommunicationLogsModel'
import InlineErrorBanner from '../InlineErrorBanner/InlineErrorBanner'
import LogsFiltersContent from './LogsFiltersContent'
import useLogs, { LOG_SORT_OPTIONS } from './hooks/useLogs'
import s from './Logs.module.scss'

const LogStatusPill = ({ status }) => (
	<StatusPill tone={STATUS_PILL_TONE[status] || status}>
		{LOG_STATUS_LABELS[status] || status}
	</StatusPill>
)

const ContactTypePill = ({ type }) => (
	<span className={s.typePill}>{CONTACT_TYPE_LABELS[type] || type || '—'}</span>
)

const ExpandedRecipients = ({ event }) => {
	const recipients = event.recipients || []

	return (
		<div className={s.expanded}>
			<div className={s.expanded__panel}>
				<div className={s.expanded__title}>Recipients ({recipients.length})</div>
				<div className={s.expanded__tableWrap}>
					<table className={s.expanded__table}>
						<thead>
							<tr>
								<th className={s.expanded__head}>Name</th>
								<th className={s.expanded__head}>Email</th>
								<th className={s.expanded__head}>Type</th>
								<th className={s.expanded__head}>Status</th>
								<th className={s.expanded__head}>Error</th>
							</tr>
						</thead>
						<tbody>
							{recipients.map(recipient => (
								<tr key={recipient.id}>
									<td className={s.expanded__cell}>
										<span className={s.expanded__name}>
											{recipient.name || '—'}
										</span>
									</td>
									<td className={s.expanded__cell}>
										<span className={s.expanded__email}>
											{recipient.email || '—'}
										</span>
									</td>
									<td className={s.expanded__cell}>
										<ContactTypePill type={recipient.type} />
									</td>
									<td className={s.expanded__cell}>
										<LogStatusPill status={recipient.status} />
									</td>
									<td className={s.expanded__cell}>
										{recipient.error_reason ? (
											<span className={s.error}>
												<span className={s.error__reason}>
													{recipient.error_reason}
												</span>
											</span>
										) : (
											<span className={s.errorEmpty}>—</span>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

export const Logs = () => {
	const {
		filteredEvents,
		statusCounts,
		tabs,
		activeGrade,
		setActiveGrade,
		quickStatus,
		quickStatusOptions,
		setQuickStatusFilter,
		isFilterOpen,
		openFilter,
		closeFilter,
		draftFilters,
		onChangeFilterKey,
		onChangeFilterField,
		applyFilters,
		clearAllPanelFilters,
		filterChips,
		removeFilterChip,
		isEmptyDataset,
		isEmptyFiltered,
		expandedRowIds,
		toggleExpand,
		studentFilterOptions,
		notificationFilterOptions,
		statusFilterOptions,
		recipientFilterOptions,
		sortKey,
		sortDir,
		setSort,
		logsLoadStatus,
		logsError,
		loadLogs,
		dismissLogsError,
	} = useLogs()

	const isLoading = logsLoadStatus === 'loading'
	const showContent = logsLoadStatus === 'success' || (isLoading && !logsError)

	const columns = useMemo(
		() => [
			{
				id: 'student',
				label: 'Student',
				sortable: true,
				isSortActive: sortKey === 'student',
				sortDir,
				sortOptions: LOG_SORT_OPTIONS.student,
				onSortSelect: opt =>
					setSort(opt.key, opt.dir, `Student: ${opt.label}`),
				headerClassName: s.colStudentHeader,
				cellClassName: s.colStudent,
				render: row => (
					<div className={s.student}>
						<InitialAvatar
							name={row.student.name}
							studentId={row.student.id}
							avatarKey={row.student.avatarKey}
							avatarSource={row.student.avatarSource}
							photoUrl={row.student.photoUrl}
							size={34}
						/>
						<span className={s.student__text}>
							<span className={s.student__name}>{row.student.name}</span>
							{row.student.grade ? (
								<span className={s.student__grade}>{row.student.grade}</span>
							) : null}
						</span>
					</div>
				),
			},
			{
				id: 'recipients',
				label: 'Recipients',
				sortable: true,
				isSortActive: sortKey === 'recipients',
				sortDir,
				sortOptions: LOG_SORT_OPTIONS.recipients,
				onSortSelect: opt =>
					setSort(opt.key, opt.dir, `Recipients: ${opt.label}`),
				headerClassName: s.colRecipientsHeader,
				cellClassName: s.colRecipients,
				render: row => {
					const summary = formatRecipientsSummary(row.recipients)
					return (
						<span className={s.recipients}>
							<span className={s.recipients__names}>{summary.names}</span>
							<span className={s.recipients__count}>{summary.countLabel}</span>
						</span>
					)
				},
			},
			{
				id: 'notification',
				label: 'Notification',
				sortable: true,
				isSortActive: sortKey === 'notification',
				sortDir,
				sortOptions: LOG_SORT_OPTIONS.notification,
				onSortSelect: opt =>
					setSort(opt.key, opt.dir, `Notification: ${opt.label}`),
				cellClassName: s.colNotification,
				render: row => row.notification?.name || '—',
			},
			{
				id: 'status',
				label: 'Status',
				sortable: true,
				isSortActive: sortKey === 'status',
				sortDir,
				sortOptions: LOG_SORT_OPTIONS.status,
				onSortSelect: opt => setSort(opt.key, opt.dir, `Status: ${opt.label}`),
				cellClassName: s.colStatus,
				render: row => <LogStatusPill status={row.status} />,
			},
			{
				id: 'created_at',
				label: 'Date & time',
				sortable: true,
				isSortActive: sortKey === 'created_at',
				sortDir,
				sortOptions: LOG_SORT_OPTIONS.created_at,
				onSortSelect: opt =>
					setSort(opt.key, opt.dir, `Date & time: ${opt.label}`),
				cellClassName: s.colDate,
				render: row => formatLogDateTime(row.created_at),
			},
			{
				id: 'expand',
				label: '',
				width: '52px',
				headerClassName: s.colExpandHeader,
				cellClassName: s.colExpand,
				headerRender: () =>
					!isFilterOpen ? (
						<button
							type="button"
							className={s.filterBtn}
							onClick={e => {
								e.stopPropagation()
								openFilter()
							}}
							aria-label="Open filters"
						>
							<span className={s.filterBtn__icon} aria-hidden>
								<FilterIcon />
							</span>
						</button>
					) : null,
				render: (row, { isExpanded }) => (
					<span
						className={`${s.expandIcon} ${isExpanded ? s.expandIconOpen : ''}`.trim()}
						aria-hidden
					>
						<span className={s.expandIcon__glyph}>
							<ChevronIcon />
						</span>
					</span>
				),
			},
		],
		[isFilterOpen, openFilter, setSort, sortDir, sortKey],
	)

	const emptyText = isLoading
		? 'Loading logs…'
		: isEmptyDataset
			? 'No delivery logs yet'
			: isEmptyFiltered
				? 'No logs match these filters'
				: '—'

	return (
		<div className={s.container}>
			<div className={s.wrap}>
				{isLoading && !logsError ? (
					<p className={s.loadingText}>Loading logs…</p>
				) : null}

				{logsError ? (
					<div className={s.errorWrap}>
						<InlineErrorBanner
							message={logsError}
							onClose={dismissLogsError}
							onRetry={loadLogs}
							retrying={isLoading}
						/>
					</div>
				) : null}

				{showContent ? (
					<>
						<div className={s.toolbar}>
							<div className={s.tabs}>
								<ListsTabs
									className={s.listsTabs}
									badgeIcon={ListIcon}
									badgeText="Lists:"
									selectPrefixIcon={ListIcon}
									selectPrefixText="Lists:"
									tabs={tabs}
									activeTab={activeGrade}
									onSelect={setActiveGrade}
								/>
							</div>

							<div className={s.pills}>
								{quickStatusOptions.map(option => {
									const isAll = option.id === 'all'
									const count = isAll
										? statusCounts.all
										: statusCounts[option.id] || 0
									const isActive = quickStatus === option.id
									const className = [
										s.pill,
										s[`pill_${option.id}`],
										isActive ? s.pillActive : '',
									]
										.filter(Boolean)
										.join(' ')

									return (
										<button
											key={option.id}
											type="button"
											className={className}
											onClick={() => setQuickStatusFilter(option.id)}
											aria-pressed={isActive}
										>
											{isAll ? (
												<span className={s.pillIcon} aria-hidden>
													<ListIcon />
												</span>
											) : (
												<span className={s.pillDot} aria-hidden />
											)}
											<span className={s.pillLabel}>{option.label}</span>
											<span className={s.pillCount}>{count}</span>
										</button>
									)
								})}
							</div>

							<FiltersBar
								chips={filterChips}
								onRemoveChip={removeFilterChip}
								onClearAll={clearAllPanelFilters}
							/>

							{!isFilterOpen ? (
								<button
									type="button"
									className={s.filtersMobileBtn}
									onClick={openFilter}
									aria-label="Open filters"
								>
									<span className={s.filtersMobileBtn__icon} aria-hidden>
										<FilterIcon />
									</span>
									<span>Filters</span>
								</button>
							) : null}
						</div>

						<div className={s.tableArea}>
							<DataTable
								variant="light"
								className={s.table}
								columns={columns}
								rows={isLoading ? [] : filteredEvents}
								getRowId={row => row.id}
								emptyText={emptyText}
								expandedRowIds={expandedRowIds}
								onToggleExpand={toggleExpand}
								onRowClick={row => toggleExpand(row.id)}
								renderExpandedRow={row => <ExpandedRecipients event={row} />}
								minWidth={860}
							/>
						</div>
					</>
				) : null}
			</div>

			<FiltersPanel isOpen={isFilterOpen} onClose={closeFilter}>
				<LogsFiltersContent
					draftFilters={draftFilters}
					studentOptions={studentFilterOptions}
					notificationOptions={notificationFilterOptions}
					statusOptions={statusFilterOptions}
					recipientOptions={recipientFilterOptions}
					onChangeKey={onChangeFilterKey}
					onChangeField={onChangeFilterField}
					onApply={applyFilters}
					onClose={closeFilter}
				/>
			</FiltersPanel>
		</div>
	)
}

export default Logs
